import React, { useEffect, useCallback, useState } from "react";
import styled, { css } from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFlag,
  faClock,
  faFileArchive,
} from "@fortawesome/free-regular-svg-icons";
import {
  faCaretRight,
  faFlagCheckered,
} from "@fortawesome/free-solid-svg-icons";
import { hoverTips } from "utils/mixin";
import data from "services/data";
import date from "utils/date";
import logger from "utils/logger";
import ToolGroup from "./toolGroup";
import DepList from "./deplist";
import notifier from "utils/notifier";

const StyledRelease = styled.div`
  display: flex;
  position: relative;
`;

const StyledSide = styled.div`
  padding: 24px 16px;
  width: 200px;
`;

const StyledIcon = styled(FontAwesomeIcon)``;

const StyledSideItem = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, Segoe UI, Helvetica, Arial,
    sans-serif, Apple Color Emoji, Segoe UI Emoji;
  font-size: ${({ theme }) => theme.fontSize.lsmall};
  color: ${({ theme, themeColor }) =>
    themeColor ? theme[themeColor] : theme.infoGray};
  line-height: 1.5;
  ${({ letterSpacing }) =>
    letterSpacing
      ? css`
          letter-spacing: ${letterSpacing};
        `
      : ""}
  text-align: right;
  word-break: break-all;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  column-gap: 6px;
`;

const StyledSideItemDetail = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`;

const StyledContent = styled.div`
  flex: 1;
  padding: 24px 16px;
  border-left: 2px solid ${({ theme }) => theme.borderGray};
  display: flex;
  flex-direction: column;
`;

const StyledContentInfo = styled.div`
  padding-bottom: 16px;
  line-height: 1.5;
`;

const StyledVersion = styled.div`
  font-size: ${({ theme }) => theme.fontSize.xxlarge};
  color: ${({ theme }) => theme.black};
`;

const StyledInfo = styled.div`
  font-size: ${({ theme }) => theme.fontSize.lsmall};
  color: ${({ theme }) => theme.infoGray};
  margin-top: 4px;
  margin-bottom: 24px;
`;

const StyledNameContainer = styled.div`
  display: flex;
  column-gap: 7px;
`;

const StyledPropertyDesc = styled.div`
  color: ${({ theme }) => theme.quoteGray};
`;

const StyledName = styled.div`
  font-size: 15px;
`;

const StyledHashContainer = styled.div`
  display: flex;
  column-gap: 7px;
  font-size: 14px;
`;

const StyledHashPropertyDesc = styled.div`
  color: ${({ theme }) => theme.quoteGray};
`;

const StyledHash = styled.div`
  color: ${({ theme }) => theme.hashBlue};
`;

const StyledContentFiles = styled.div`
  border-top: 1px solid ${({ theme }) => theme.borderGray};
  padding: 16px 0 24px 0;
  display: flex;
  flex-direction: column;
  row-gap: 16px;
`;

const StyledFilersHeader = styled.div`
  color: ${({ theme }) => theme.black};
  width: fit-content;
  user-select: none;
  cursor: pointer;
  ${StyledIcon} {
    transition: 0.3s;
  }
  transition: 0.4s;

  ${({ active }) =>
    active
      ? css`
          ${StyledIcon} {
            transform: rotate(90deg);
          }
        `
      : css`
          ${StyledIcon} {
            transform: rotate(0);
          }
        `}
`;

const StyledFilesContentClip = styled.div`
  overflow: hidden;
  position: relative;
`;

const StyledFilesContent = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${({ theme }) => theme.borderGray};
  border-radius: 6px;
  overflow: hidden;
  position: absolute;
  transition: 0.3s ease-in-out;
  ${({ active }) =>
    active
      ? css`
          position: relative;
          transform: translateY(0);
        `
      : css`
          transform: translateY(-100%);
        `}
`;

const StyledFile = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.borderGray};
  :last-child {
    border-bottom: none;
  }
  padding: 8px;
  color: ${({ theme }) => theme.black};
  display: flex;
  justify-content: space-between;
`;

const StyledFileName = styled.div`
  cursor: pointer;
  font-size: ${({ theme }) => theme.fontSize.lsmall};
  color: unset;
  text-decoration: none;
  ${StyledIcon} {
    margin-right: 8px;
  }

  position: relative;

  ::after {
    content: "";
    width: 100%;
    height: 1px;
    position: absolute;
    left: 0;
    bottom: -2px;
    background-color: ${({ theme }) => theme.fontColor};
    transition: 0.2s;
    transform: scaleX(0);
  }

  :hover {
    ::after {
      transform: scaleX(1);
    }
  }
`;

const StyledFileTail = styled.div`
  display: flex;
  align-items: center;
  column-gap: 7px;
`;

const StyledToolBox = styled.div`
  position: absolute;
  right: 7px;
  top: 7px;
`;

const StyledId = styled.div`
  position: absolute;
  left: 4px;
  top: 6px;
  font-family: ${({ theme }) => theme.font.youshe};
  color: ${({ theme }) => theme.infoBlue};
`;

const publishFlagIcons = {
  1: faFlagCheckered,
  2: faFlag,
};

const Release = ({
  auth,
  id,
  publishFlagDesc,
  publishFlag,
  buildTime,
  pluginVerHash,
  publishTime,
  version,
  changeInfo,
  pluginAppId,
  module,
  fileId,
  fileName,
  fileSize,
  depPluginVerList = [],
  tools,
  fileTools,
  reload,
  publishSystem,
}) => {
  const [openFiles, setOpenFiles] = useState(false);
  const [pluginInfo, setPluginInfo] = useState(null);

  // 获取插件的信息, 就是插件的信息 不是插件版本的信息
  const getPluginInfo = useCallback(async (appId) => {
    if (!appId) return;
    try {
      const plugin = await data.getPluginInfoByAppId(appId);
      console.log('plugin',plugin);
      setPluginInfo(plugin);
    } catch (e) {
      notifier.error(e.message);
    }
  }, []);
  useEffect( () => {
    getPluginInfo(pluginAppId)
  },[pluginAppId])
  const pluginVerData = {
    id,
    pluginAppId,
    publishFlagDesc,
    publishFlag,
    buildTime,
    pluginVerHash,
    publishTime: date.formatTime("yyyy-MM-dd hh:mm:ss", new Date(publishTime)),
    version,
    changeInfo,
    fileId,
    fileName,
    fileSize: (fileSize / 1024 / 1024).toFixed(3) + "MB",
    depPluginVerList,
    publishSystem,
  };
  return (
    <StyledRelease>
      <StyledId title={`唯一ID: ${id}`}>#{id}</StyledId>
      <StyledToolBox>
        <ToolGroup auth={auth} tools={tools} data={pluginVerData} />
      </StyledToolBox>
      <StyledSide>
        <StyledSideItem letterSpacing="1.5px" title="publish flag 发布标志">
          {/* 1 正式版, 2 测试版 */}
          <StyledIcon icon={publishFlagIcons[publishFlag]} size="sm" />
          {publishSystem}-{publishFlagDesc}
        </StyledSideItem>
        <StyledSideItem themeColor="blue" title="build time 编译时间">
          {buildTime}
        </StyledSideItem>
        <StyledSideItem themeColor="green" title="publish time 发布时间">
          <StyledIcon icon={faClock} />
          {/* publishTime如果添加时传毫秒数的话 传到后端后 会少3位 这里*1000补回来 临时处理 */}
          {pluginVerData.publishTime}
        </StyledSideItem>
      </StyledSide>
      <StyledContent>
        <StyledContentInfo>
          <StyledVersion>{version}</StyledVersion>
          <StyledInfo>{changeInfo}</StyledInfo>

          <StyledNameContainer>
            <StyledPropertyDesc>From:</StyledPropertyDesc>
            <StyledName>{`${!!module ? module : pluginInfo?.module }（${pluginAppId}）`}</StyledName>
          </StyledNameContainer>

          <StyledHashContainer>
            <StyledHashPropertyDesc>Hash:</StyledHashPropertyDesc>
            <StyledHash>{pluginVerHash}</StyledHash>
          </StyledHashContainer>
        </StyledContentInfo>

        {/* 版本依赖列表 */}
        <DepList
          auth={auth}
          pluginVerData={pluginVerData}
          onDepChange={reload}
        />

        <StyledContentFiles>
          <StyledFilersHeader
            active={openFiles}
            onClick={() => setOpenFiles(!openFiles)}
            title={openFiles ? "收起" : "展开"}
          >
            <StyledIcon icon={faCaretRight} /> Files
          </StyledFilersHeader>
          <StyledFilesContentClip>
            <StyledFilesContent active={openFiles}>
              {fileId && (
                <StyledFile>
                  <StyledFileName
                    title="点击下载"
                    // onClick={() => {
                    //   data.downloadFile(fileId, fileName);
                    // }}
                    as="a"
                    href={data.getDownloadFileUrl(fileId)}
                    download={fileName}
                  >
                    <StyledIcon icon={faFileArchive} />
                    {fileName}
                  </StyledFileName>
                  <StyledFileTail>
                    {pluginVerData.fileSize}
                    <ToolGroup
                      auth={auth}
                      tools={fileTools}
                      data={{ fileId, fileName, fileSize }}
                    />
                  </StyledFileTail>
                </StyledFile>
              )}
            </StyledFilesContent>
          </StyledFilesContentClip>
        </StyledContentFiles>
      </StyledContent>
    </StyledRelease>
  );
};

export default Release;
